import { ChildProps } from '@apollo/client/react/hoc';
import { parseKeywords } from '@reverbdotcom/commons/src/components/ads/ad_banner_helpers';
import { adBannerQuery } from '@reverbdotcom/commons/src/components/ads/ad_fragments';
import { IUserContext, withUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import { MParticlePageName } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { EppoExperimentValues, isEppoExpEnabled } from '@reverbdotcom/commons/src/eppo/eppoExperimentHelpers';
import {
  CoreAdBannerWrapper,
} from '@reverbdotcom/commons/src/gql/graphql';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import React from 'react';
import { RouteProps, withRouter, WithRouterProps } from 'react-router';
import MarketplacePage from './marketplace_page';
import { useEppoAssignment, useEppoClient } from '@reverbdotcom/commons/src/user_hooks';
import eppoFlags from '@reverbdotcom/commons/src/eppoFlags';
import { isEU, isUSShippingRegion } from '../../lib/geo';
import MarketplacePageV2 from './v2/marketplace_page_v2';
import { US_CON, currencies } from '@reverbdotcom/commons/src/constants';

interface IExternalProps {
  emptyGrid?: JSX.Element;
  showSold?: boolean;
  pageName?: MParticlePageName;
  route?: RouteProps<PropsFromRoute>;
  showFlags?: boolean;
  includeItemListMetadata?: boolean;
  v2Marketplace?: boolean;
}

interface PropsFromRoute {
  pageName: MParticlePageName;
  v2Marketplace?: boolean;
}

type IProps = WithRouterProps & IUserContext & IExternalProps & ChildProps<{}, CoreAdBannerWrapper.Query>;

const adQuery = withGraphql<IProps, CoreAdBannerWrapper.Query, CoreAdBannerWrapper.Variables>(adBannerQuery,
  {
    options: (ownProps) => {
      return {
        ssr: true,
        skip: ownProps.route?.props?.v2Marketplace && ownProps.user.isAdmin, // Skip ad query for v2 marketplace
        variables: { keywords: parseKeywords(ownProps.location, ownProps.params) },
      };
    },
  },
);

export function MarketplacePageWrapper({
  data,
  params,
  location,
  pageName = MParticlePageName.Marketplace,
  showSold = false,
  emptyGrid = null,
  route,
  showFlags = false,
  includeItemListMetadata = false,
  user,
}: IProps) {
  const eppoClient = useEppoClient();
  const userEligibleForGreatValueFilter = pageName === MParticlePageName.Marketplace &&
    ((isUSShippingRegion(user?.shippingRegionCode) && user?.currency == 'USD') ||
      (user?.shippingRegionCode === 'GB' && user?.currency == 'GBP') ||
      (isEU(user?.shippingRegionCode) && user?.currency == 'EUR'));

  const greatValueFilterEnabled = useEppoAssignment({
    flagKey: eppoFlags.WEB_GREAT_VALUE_FILTER,
    forceReturnValue: userEligibleForGreatValueFilter ? null : EppoExperimentValues.Control,
  }) === 'treatment';

  const isPrecomputedWinnerExpEnabled = user?.shippingRegionCode === US_CON &&
    user?.currency === currencies.USD &&
    isEppoExpEnabled({ flagKey: eppoFlags.PRECOMPUTED_FEATURED_LISTING_WINNER, user, eppoClient });

  const bumpInSortedMarketplaceExpGroup = useEppoAssignment({ flagKey: eppoFlags.BUMP_IN_SORTED_MARKETPLACE });

  const searchQueryMigrationEnabled = useEppoAssignment({
    flagKey: eppoFlags.WEB_SEARCH_QUERY_MIGRATION,
  }) === 'treatment';
  const legacyEnabled = location?.query?.legacy === 'true';

  const hoistFavoritesEnabled = useEppoAssignment({
    flagKey: eppoFlags.WEB_MARKETPLACE_HOIST_FAVORITES,
  }) === 'treatment';

  if ((route?.props?.v2Marketplace && user.isAdmin) || (searchQueryMigrationEnabled && !legacyEnabled)) {
    return (
      <MarketplacePageV2 />
    );
  }

  return (
    <MarketplacePage
      params={params}
      pageName={route?.props?.pageName || pageName}
      showSold={showSold}
      emptyGrid={emptyGrid}
      adLoading={data?.loading}
      ad={data?.adServe?.ad}
      showFlags={showFlags}
      includeItemListMetadata={includeItemListMetadata}
      greatValueFilterEnabled={greatValueFilterEnabled}
      isPrecomputedWinnerExpEnabled={isPrecomputedWinnerExpEnabled}
      bumpInSortedMarketplaceExpGroup={bumpInSortedMarketplaceExpGroup}
      hoistFavoritesEnabled={hoistFavoritesEnabled}
    />
  );
}

export default withUserContext<IExternalProps>(withRouter(adQuery(MarketplacePageWrapper)));
